import moment from "moment";

// Utilidades de rango de fechas compartidas entre los stores de Resultados,
// Ventas y Gastos. Mantener las dos funciones aquí evita que cada store
// duplique la misma lógica de switch + buildMonthHeaders.

// Granularidad year/ytd no respeta el `selectedPeriodRange` — siempre muestra
// los últimos N años calendario hacia atrás como columnas (decisión de UX
// compartida por todos los reportes). Mover el número acá centraliza el
// "cuántos años hacia atrás" para que no se desincronice entre tabs.
export const YEARS_SHOWED = 4;

export function resolveDateRange(endMonth, selectedPeriodRange, filterPeriod = null) {
    const refEnd = moment(endMonth).endOf("month");
    let initDate;
    let endDate = refEnd.format("YYYY-MM-DD");

    // Para "lastYear"/"last24months" usamos subtract(N-1) en lugar de N: el
    // rango [initDate..endDate] es inclusivo de ambos extremos, así que para
    // ver exactamente N meses (endMonth incluido) hay que retroceder N-1.
    // subtract(12) producía 13 cabeceras (1 + 12 meses anteriores), que no
    // matchea la semántica del label "Último año".
    switch (selectedPeriodRange) {
        case "lastYear":
            initDate = moment(refEnd).subtract(11, "months").startOf("month").format("YYYY-MM-DD");
            break;
        case "last24months":
            initDate = moment(refEnd).subtract(23, "months").startOf("month").format("YYYY-MM-DD");
            break;
        case "actualYear":
            initDate = moment(refEnd).startOf("year").format("YYYY-MM-DD");
            break;
        case "previousYear": {
            // Año calendario anterior al de refEnd — self-contained: el rango
            // se computa sin depender de que `setPeriodRange` haya pre-shifteado
            // endMonth a Dec del año previo. Antes la fórmula era idéntica a
            // `actualYear` y solo funcionaba por la coincidencia de que
            // RANGE_OVERRIDES movía endMonth; cuando el usuario cambiaba la
            // fecha por el calendario después de elegir "Año Anterior",
            // la query terminaba pidiendo Jan→endMonth del año en curso.
            const prevYearRef = moment(refEnd).subtract(1, "year");
            initDate = prevYearRef.clone().startOf("year").format("YYYY-MM-DD");
            endDate = prevYearRef.clone().endOf("year").format("YYYY-MM-DD");
            break;
        }
        default:
            initDate = moment(refEnd).subtract(11, "months").startOf("month").format("YYYY-MM-DD");
    }

    // Override de granularidad multi-año: cuando el filtro es year o ytd, los
    // stores quieren mostrar `YEARS_SHOWED` años calendario hacia atrás como
    // columnas — ignorando el `selectedPeriodRange`. Antes este bloque vivía
    // duplicado en cada uno de los 6 stores de reportes; lo movimos acá para
    // que el contrato sea único.
    if (filterPeriod === "year" || filterPeriod === "ytd") {
        initDate = moment(refEnd)
            .subtract(YEARS_SHOWED, "year")
            .startOf("year")
            .format("YYYY-MM-DD");
    }

    return { initDate, endDate };
}

export function buildMonthHeaders(initDate, endDate) {
    const headers = [];
    let cursor = moment(initDate).startOf("month");
    const end = moment(endDate).startOf("month");

    while (cursor.isSameOrBefore(end)) {
        headers.push(cursor.format("MMM YYYY").replace(".", ""));
        cursor.add(1, "months");
    }
    return headers;
}

// Formato de etiqueta de período según granularidad solicitada.
// month → "ene 2025", quarter → "Q1 2025", year/ytd → "2025".
export function formatPeriodLabel(period, filterPeriod) {
    const m = moment(period);
    switch (filterPeriod) {
        case "quarter": return m.format("[Q]Q YYYY").replace(".", "");
        case "year":
        case "ytd": return m.format("YYYY").replace(".", "");
        case "month":
        default: return m.format("MMM YYYY").replace(".", "");
    }
}

// Cabeceras únicas dentro del rango, respetando la granularidad solicitada.
// Recorre mes a mes y deduplica etiquetas (Q1 2025 aparece una sola vez aunque
// el rango cubra ene/feb/mar).
export function buildHeadersByPeriod(initDate, endDate, filterPeriod) {
    const headers = [];
    let cursor = moment(initDate).startOf("month");
    const end = moment(endDate).startOf("month");

    while (cursor.isSameOrBefore(end)) {
        const label = formatPeriodLabel(cursor, filterPeriod);
        if (!headers.includes(label)) {
            headers.push(label);
        }
        cursor.add(1, "months");
    }
    return headers;
}
